import type { VariationPlacement } from "@popperjs/core";
import clsx from "clsx";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Popper } from "./Popper";

export type ClickPopperProps = {
  title?: ReactNode;
  placement?: VariationPlacement;
  flip?: boolean;
  clickable?: boolean;
  disabled?: boolean;
  className?: string;
  titleClassName?: string;
  popperClassName?: string;
  onShow?: () => void;
  onHide?: () => void;
  children?: ReactNode;
};

export const ClickPopper = ({
  title,
  placement = "bottom-start",
  flip = true,
  clickable = true,
  disabled = false,
  titleClassName,
  popperClassName,
  onShow,
  onHide,
  children,
  className,
}: ClickPopperProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onHide?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onHide]);

  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!disabled) {
      e.stopPropagation();
      e.preventDefault();
      setIsOpen(!isOpen);
      if (!isOpen) {
        onShow?.();
      } else {
        onHide?.();
      }
    }
  };

  if (!title) return <>{children}</>;

  return (
    <span ref={anchorRef} className={clsx(className)} onClick={handleClick}>
      <Popper
        placement={placement}
        open={isOpen && !disabled}
        anchorEl={anchorRef.current}
        allowOverflow={!flip}
        clickable={clickable}
        className={popperClassName}
      >
        <div
          ref={tooltipRef}
          className={clsx(
            "relative text-sm bg-stone-100 dark:bg-[#181818] text-stone-800 dark:text-stone-100 border-2 border-stone-800 dark:border-stone-200 border-opacity-30 max-w-[300px] z-10001",
            titleClassName,
          )}
        >
          {title}
        </div>
      </Popper>
      {children}
    </span>
  );
};
